<template>
  <div class="scene-wrapper">
    <div ref="threeContainer" class="three-container"></div>
    <div class="info-overlay">
      <div class="sensor-name mt-1">
        <strong>Fastner ID:</strong> {{ sensorName }}<br />
        <span class="mt-0" style="font-size: 16px; font-weight: 200"
          ><strong></strong> {{ sensorLocation }}</span
        >
      </div>
      <div class="sensor-location"></div>
      <div class="scene-replay-button float-left" @click="replayAnimation">
        <v-icon color="white">mdi-refresh</v-icon>
      </div>

      <!-- Left Side Cards -->
      <div class="left-cards">
        <!-- Torque & Rotation Card -->
        <div class="info-card torque-rotation">
          <div class="card-title">Torque & Rotation</div>
          <div class="metric">
            <div class="label">Initial Torque</div>
            <div class="value">{{ displayInitialTorque }} {{ torqueUnit }}</div>
          </div>
          <div class="metric">
            <div class="label">Current Torque</div>
            <div class="value">{{ displayCurrentTorque }} {{ torqueUnit }}</div>
          </div>
          <div class="metric">
            <div class="label">Initial Angle</div>
            <div class="value">{{ startRotation }}°</div>
          </div>
          <div class="metric">
            <div class="label">Torque Angle Deviation</div>
            <div class="value">{{ endRotation }}°</div>
          </div>
        </div>
        <div class="info-card torque-rotation">
          <div class="card-title">Loosening Rate</div>
          <div class="metric">
            <div class="label">Loosening Rate</div>
            <div class="value">
              {{ displayLooseningRate }} {{ torqueUnit }}/°
            </div>
          </div>
        </div>
        <div class="info-card torque-rotation status-details-btn"  @click="handleStatusClick">
          <div class="metric status">
            <div class="card-title">Status</div>
            <div
              class="value"
              :class="{
                warning: status === 'Warning',
                secure: status === 'Secure',
                critical: status === 'Critical',
              }"
            >
              <v-icon
                class="mr-1"
                size="25"
                :color="
                  status === 'Warning'
                    ? 'amber'
                    : status === 'Secure'
                    ? 'green'
                    : 'red'
                "
              >
                {{
                  status === "Warning"
                    ? "mdi-alert"
                    : status === "Secure"
                    ? "mdi-check-circle"
                    : "mdi-alert-octagon"
                }}
              </v-icon>
              {{ statusMessage }}
              
            </div>
          </div>
        </div>
      </div>

      <!-- Right Side Cards -->
      <div class="right-cards">
        <!-- Actionable Insights Card -->
        <div class="info-card actionable-insights">
          <div class="card-title">Actionable Insights</div>
          <div class="metric">
            <div class="label">Torque Deviation</div>
            <div class="value">{{ torqueDeviation }} {{ torqueUnit }}</div>
          </div>
          <!-- <div class="metric">
            <div class="label">Rotation Deviation</div>
            <div class="value">{{ rotationDeviation }}°</div>
          </div> -->
          <div class="metric risk">
            <div class="label">Risk</div>
            <div
              class="value"
              :style="{
                color:
                  status === 'Warning'
                    ? 'yellow'
                    : status === 'Secure'
                    ? 'green'
                    : 'red',
              }"
            >
              {{ riskMessage }}
            </div>
          </div>
        </div>

        <!-- Inspection Info -->
        <div class="inspection-info">
          <div class="inspection-cards">
            <div class="last-inspection">
              <div class="label">Last Inspection</div>
              <div class="value">
                {{ lastInspection }} <span>Days Ago</span>
              </div>
            </div>
            <div class="next-inspection">
              <div class="label">Next Inspection</div>
              <div class="value">{{ nextInspection }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import Vue from 'vue';

// Create a global event bus just for status click events
export const StatusBus = new Vue();

export default {
  name: "BoltScene",
  model: {
    prop: "rotation",
    event: "update:rotation",
  },
  props: {
    status: {
      type: String,
      default: "Warning",
    },
    statusMessage: {
      type: String,
      default: "Loosening Detected",
    },
    rotation: {
      type: Number,
      default: 0,
    },
    initialTorque: {
      type: Number,
      default: 120,
    },
    currentTorque: {
      type: Number,
      default: 110,
    },
    looseningRate: {
      type: Number,
      default: -5,
    },
    startRotation: {
      type: Number,
      default: 0,
    },
    endRotation: {
      type: Number,
      default: 42,
    },
    rotationAxis: {
      type: String,
      default: "y",
      validator: (value) => ["x", "y", "z"].includes(value),
    },
    autoRotate: {
      type: Boolean,
      default: false,
    },
    rotationSpeed: {
      type: Number,
      default: 1,
    },
    isAnimating: {
      type: Boolean,
      default: false,
    },
    thresholds: {
      type: Object,
      default: () => ({
        green: 5,
        yellow: 15,
        red: 60,
      }),
    },
    sensorName: {
      type: String,
      default: "ximu3-001",
    },
    sensorLocation: {
      type: String,
      default: "Pumping Station, Sector 5",
    },
    lastInspection: {
      type: Number,
      default: 0,
    },
    nextInspection: {
      type: String,
      default: "",
    },
    torqueUnit: {
      type: String,
      default: "Nm",
      validator: (value) => ["Nm", "ft-lbs"].includes(value),
    },
  },
  data() {
    return {
      scene: null,
      camera: null,
      renderer: null,
      controls: null,
      bolt: null,
      animationId: null,
      clock: null,
      pathLine: null,
      degreeCanvas: null,
      degreeContext: null,
      degreeSprite: null,
      gridHelper: null,
      previousRotationAxis: "y",
      degreesGroup: null,
      rotationIndicator: null,
      boltAutoRotationSpeed: 0, // degrees per second for subtle rotation
    };
  },
  computed: {
    // Conversion factor for Nm to lb-ft
    conversionFactor() {
      return 0.737562;
    },

    // Convert initial torque based on selected unit
    displayInitialTorque() {
      const value =
        this.torqueUnit === "Nm"
          ? this.initialTorque
          : this.initialTorque * this.conversionFactor;
      return Number(value.toFixed(1));
    },

    // Convert current torque based on selected unit
    displayCurrentTorque() {
      const value =
        this.torqueUnit === "Nm"
          ? this.currentTorque
          : this.currentTorque * this.conversionFactor;
      return Number(value.toFixed(1));
    },

    // Convert loosening rate based on selected unit
    displayLooseningRate() {
      if (this.looseningRate === undefined || this.looseningRate === null) return 0;
      
      const numValue = Number(this.looseningRate);
      if (isNaN(numValue)) return 0;
      
      const value =
        this.torqueUnit === "Nm"
          ? numValue
          : numValue * this.conversionFactor;
      return Number(value.toFixed(1));
    },

    torqueDeviation() {
      if (this.currentTorque === undefined || this.initialTorque === undefined) {
        return 0;
      }
      
      const value = Number(this.currentTorque) - Number(this.initialTorque);
      
      return this.torqueUnit === "Nm"
        ? Number(value.toFixed(1))
        : Number((value * this.conversionFactor).toFixed(1));
    },

    rotationDeviation() {
      return this.endRotation - this.startRotation;
    },

    riskMessage() {
      return `Fastener loosening at a rate of ${Math.abs(
        this.displayLooseningRate
      )} ${this.torqueUnit} per degree. Immediate inspection recommended.`;
    },
  },
  watch: {
    rotation() {
      this.updateRotation();
    },
    rotationAxis() {
      this.updateRotationAxis();
    },
    isAnimating(newVal, oldVal) {
      // console.log(`isAnimating changed from ${oldVal} to ${newVal}`);
      if (newVal) {
        this.startBoltAnimation();
      }
    },
  },
  methods: {
    handleStatusClick() {
      if (this.$parent && typeof this.$parent.showLatestAlert === 'function') {
        this.$parent.showLatestAlert();
      } else {
        this.$root.$emit('bolt-status-clicked');
        StatusBus.$emit('status-clicked');
      }
    },

    initThree() {
      // Initialize clock for animations
      this.clock = new THREE.Clock();

      // Create scene
      this.scene = new THREE.Scene();
      // Use a dark blue/purple gradient color to match the previous card style
      this.scene.background = new THREE.Color(0x000000);

      // Create camera
      this.camera = new THREE.PerspectiveCamera(
        45, // Reduced field of view for less perspective distortion
        this.$refs.threeContainer.clientWidth /
          this.$refs.threeContainer.clientHeight,
        0.1,
        1000
      );
      // Position camera directly overhead for completely flat appearance
      this.camera.position.set(0, 12, 0);
      this.camera.lookAt(0, 0, 0);

      // Create renderer
      this.renderer = new THREE.WebGLRenderer({ antialias: true });
      this.renderer.setSize(
        this.$refs.threeContainer.clientWidth,
        this.$refs.threeContainer.clientHeight
      );
      this.renderer.shadowMap.enabled = true;
      this.renderer.shadowMap.type = THREE.PCFSoftShadowMap; // Softer shadows
      this.renderer.toneMapping = THREE.ACESFilmicToneMapping; // Better metallic rendering
      this.renderer.toneMappingExposure = 1.0;
      // Use the correct color space property for the Three.js version
      this.renderer.outputEncoding = THREE.sRGBEncoding;
      this.$refs.threeContainer.appendChild(this.renderer.domElement);

      // Add orbit controls with restricted movement
      this.controls = new OrbitControls(this.camera, this.renderer.domElement);
      this.controls.enableDamping = true;
      // Keep camera more overhead for flatter gauge appearance
      this.controls.minPolarAngle = 0;
      this.controls.maxPolarAngle = Math.PI / 6; // Allow only 30 degrees from overhead (was 60°)
      this.controls.enableRotate = false; // Disable rotation
      this.controls.rotateSpeed = 0.3; // Slower rotation speed
      this.controls.enableZoom = false; // Disable zoom
      this.controls.enablePan = false; // Disable panning

      // Add lights
      const ambientLight = new THREE.AmbientLight(0x404040, 0.6); // Reduced ambient light
      this.scene.add(ambientLight);

      // Main directional light for primary illumination
      const directionalLight = new THREE.DirectionalLight(0xffffff, 1.2);
      directionalLight.position.set(5, 10, 7);
      directionalLight.castShadow = true;
      directionalLight.shadow.mapSize.width = 2048;
      directionalLight.shadow.mapSize.height = 2048;
      this.scene.add(directionalLight);

      // Add a second directional light for better metal highlighting
      const rimLight = new THREE.DirectionalLight(0xffffff, 0.8);
      rimLight.position.set(-5, 8, -5);
      this.scene.add(rimLight);

      // Add a point light for additional metallic highlights
      const pointLight = new THREE.PointLight(0xffffff, 0.6, 20);
      pointLight.position.set(0, 5, 3);
      this.scene.add(pointLight);

      // Create coordinate axes
      this.createCoordinateSystem();

      // Create bolt
      this.createBolt();

      // Start animation loop
      this.animate();

      // Handle window resize
      window.addEventListener("resize", this.onWindowResize);
    },

    createCoordinateSystem() {
      // Create circular gauge instead of grid
      const gaugeRadius = 3.2;
      const gaugeGeometry = new THREE.CircleGeometry(gaugeRadius, 64);

      // Create canvas for gauge texture
      const canvas = document.createElement("canvas");
      canvas.width = 1024;
      canvas.height = 1024;
      const ctx = canvas.getContext("2d");

      // Make background transparent
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw only white outline circle (no fill)
      ctx.strokeStyle = "white";
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.arc(512, 512, 500, 0, Math.PI * 2);
      ctx.stroke();

      // Draw regular degree markings in white (every 10 degrees)
      ctx.strokeStyle = "white";
      ctx.lineWidth = 2;

      for (let i = 0; i < 360; i += 10) {
        // Skip the cardinal directions (0, 90, 180, 270) - they'll be drawn separately
        if (i % 90 === 0) continue;
        
        const angle = ((i - 90) * Math.PI) / 180; // -90 to start at 3 o'clock
        const startRadius = i % 30 === 0 ? 420 : 450;
        const x1 = 512 + Math.cos(angle) * startRadius;
        const y1 = 512 + Math.sin(angle) * startRadius;
        const x2 = 512 + Math.cos(angle) * 480;
        const y2 = 512 + Math.sin(angle) * 480;

        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      }

      // Draw slightly bolder lines for cardinal directions (0°, 90°, 180°, 270°)
      ctx.strokeStyle = "white";
      ctx.lineWidth = 12; // More bold than regular lines (2px)
      
      for (let i = 0; i < 360; i += 90) {
        const angle = ((i - 90) * Math.PI) / 180; // -90 to start at 3 o'clock
        const startRadius = 400; // Same as major degree marks
        const x1 = 512 + Math.cos(angle) * startRadius;
        const y1 = 512 + Math.sin(angle) * startRadius;
        const x2 = 512 + Math.cos(angle) * 480;
        const y2 = 512 + Math.sin(angle) * 480;

        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      }

      // Create transparent center hole with white outline (no logo)
      ctx.strokeStyle = "white";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(512, 512, 60, 0, Math.PI * 2);
      ctx.stroke();

      // Create texture and material
      const texture = new THREE.CanvasTexture(canvas);
      const gaugeMaterial = new THREE.MeshBasicMaterial({
        map: texture,
        side: THREE.DoubleSide,
        transparent: true,
      });

      // Create mesh and add to scene
      this.gridHelper = new THREE.Mesh(gaugeGeometry, gaugeMaterial);
      this.gridHelper.rotation.x = -Math.PI / 2; // Lay completely flat
      this.gridHelper.position.y = -1.5; // Move down from the center
      this.scene.add(this.gridHelper);
      this.gridHelper.visible = true;

      // Create separate degree markers as sprites
      this.createDegreeMarkers();

      // Create path visualization
      this.createPathVisualization();
    },

    createDegreeMarkers() {
      // Create degree markers that won't rotate with the meter
      this.degreesGroup = new THREE.Group();

      // For each 30-degree increment, going counter-clockwise from 3 o'clock
      for (let i = 0; i < 360; i += 30) {
        // Create a canvas for each number
        const canvas = document.createElement("canvas");
        canvas.width = 64;
        canvas.height = 64;
        const ctx = canvas.getContext("2d");

        // Clear canvas with transparent background
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Draw text - reverse the numbering to go counter-clockwise from 3 o'clock
        const displayNumber = (360 - i) % 360;
        ctx.font = displayNumber % 90 === 0 ? "bold 32px Arial" : "24px Arial";
        ctx.fillStyle = "white"; // All numbers in white
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(displayNumber.toString(), canvas.width / 2, canvas.height / 2);

        // Create texture and sprite
        const texture = new THREE.CanvasTexture(canvas);
        const material = new THREE.SpriteMaterial({
          map: texture,
          transparent: true,
        });

        const sprite = new THREE.Sprite(material);
        sprite.userData = { initialAngle: i }; // Store the initial angle for updates

        // Position sprite around the gauge - 0 at 3 o'clock, going counter-clockwise
        const angle = ((i - 90) * Math.PI) / 180; // -90 to start at 3 o'clock
        const radius = 2.2; // Inside the gauge circumference (gauge radius is now 3.2)
        sprite.position.set(
          Math.cos(angle) * radius,
          -1.4, // Match the meter's new position
          Math.sin(angle) * radius
        );

        // Scale the sprite
        sprite.scale.set(0.7, 0.7, 1);

        // Add to the group
        this.degreesGroup.add(sprite);
      }

      // Add the group to the scene
      this.scene.add(this.degreesGroup);
    },

    createBolt() {
      // Create bolt head (hexagon) with more realistic proportions and materials
      const headGeometry = new THREE.CylinderGeometry(0.5, 0.5, 0.25, 6);
      const headMaterial = new THREE.MeshPhongMaterial({ 
        color: 0x050505,  // Dark charcoal/metallic gray
        shininess: 60,
        specular: 0x050505
      });
      const boltHead = new THREE.Mesh(headGeometry, headMaterial);
      boltHead.position.y = 0.125; // Position slightly above center

      // Create bolt shaft with more realistic threading
      const shaftGeometry = new THREE.CylinderGeometry(0.18, 0.18, 2.0, 32);
      const shaftMaterial = new THREE.MeshPhongMaterial({ 
        color: 0x050505,  // Same dark color as head
        shininess: 60,
        specular: 0x050505
      });
      const boltShaft = new THREE.Mesh(shaftGeometry, shaftMaterial);
      boltShaft.position.y = -0.875; // Position shaft below head

      // Create threaded nut (hexagonal)
      const nutGeometry = new THREE.CylinderGeometry(0.45, 0.45, 0.3, 6);
      const nutMaterial = new THREE.MeshPhongMaterial({ 
        color: 0x050505,  // Much darker metallic grey - almost black
        shininess: 120,   // Higher shininess for more metallic appearance
        specular: 0x050505, // Bright specular highlights for metallic look
        reflectivity: 0.9,   // High reflectivity for metallic appearance
        emissive: 0x050505  // No emissive to keep it dark
      });
      const nut = new THREE.Mesh(nutGeometry, nutMaterial);
      nut.position.y = -1.6; // Position at bottom of shaft

      // Create white outline for the nut
      const nutEdgesGeometry = new THREE.EdgesGeometry(nutGeometry);
      const nutEdgesMaterial = new THREE.LineBasicMaterial({ 
        color: 0xffffff, // White outline
        linewidth: 3
      });
      const nutOutline = new THREE.LineSegments(nutEdgesGeometry, nutEdgesMaterial);
      nutOutline.position.y = 0.13; // Same position as nut

      // Create threaded hole in nut
      const holeGeometry = new THREE.CylinderGeometry(0.19, 0.19, 0.35, 32);
      const holeMaterial = new THREE.MeshPhongMaterial({ 
        color: 0x1a1a1a,  // Very dark for the hole
        shininess: 30
      });
      const nutHole = new THREE.Mesh(holeGeometry, holeMaterial);
      nutHole.position.y = -1.6; // Same position as nut

      // Create detailed thread geometry for entire shaft
      const threadGroup = new THREE.Group();
      
      // Add spiral threading along the shaft
      for (let i = 0; i < 40; i++) {
        const threadRingGeometry = new THREE.TorusGeometry(0.18, 0.02, 8, 16);
        const threadRingMaterial = new THREE.MeshPhongMaterial({
          color: 0x050505,
          shininess: 40
        });
        const threadRing = new THREE.Mesh(threadRingGeometry, threadRingMaterial);
        threadRing.position.y = -0.05 - i * 0.04; // Space threads evenly
        threadRing.rotation.x = Math.PI / 2; // Rotate to horizontal
        threadGroup.add(threadRing);
      }

      // Create nut threading (internal)
      const nutThreadGroup = new THREE.Group();
      for (let i = 0; i < 8; i++) {
        const nutThreadGeometry = new THREE.TorusGeometry(0.19, 0.015, 6, 12);
        const nutThreadMaterial = new THREE.MeshPhongMaterial({
          color: 0x0f0f0f,  // Match the main nut color - very dark metallic grey
          shininess: 120    // Match the main nut shininess
        });
        const nutThread = new THREE.Mesh(nutThreadGeometry, nutThreadMaterial);
        nutThread.position.y = -1.45 - i * 0.03;
        nutThread.rotation.x = Math.PI / 2;
        nutThreadGroup.add(nutThread);
      }

      // Create washer under bolt head for extra detail
      const washerGeometry = new THREE.CylinderGeometry(0.4, 0.4, 0.02, 32);
      const washerMaterial = new THREE.MeshPhongMaterial({
        color: 0x444444,
        shininess: 90,
        specular: 0x555555
      });
      const washer = new THREE.Mesh(washerGeometry, washerMaterial);
      washer.position.y = 0; // Just below bolt head

      // Create bolt group
      this.bolt = new THREE.Group();
      this.bolt.add(boltHead);
      this.bolt.add(boltShaft);
      this.bolt.add(nut);
      this.bolt.add(nutOutline);
      this.bolt.add(nutHole);
      this.bolt.add(threadGroup);
      this.bolt.add(nutThreadGroup);
      this.bolt.add(washer);

      // Scale the bolt assembly to make it appropriately sized
      this.bolt.scale.set(2.4, 2.4, 2.4);

      // Position bolt assembly higher above the XY plane
      this.bolt.position.set(0, -0.3, 0);

      // Set up shadows for all components
      this.bolt.traverse((node) => {
        if (node.isMesh) {
          node.castShadow = true;
          node.receiveShadow = true;
        }
      });

      // Add bolt to scene
      this.scene.add(this.bolt);
      
      // Create degree text sprite
      const canvas = document.createElement("canvas");
      canvas.width = 256;
      canvas.height = 128;
      this.degreeCanvas = canvas;
      this.degreeContext = canvas.getContext("2d");

      const texture = new THREE.CanvasTexture(canvas);
      const spriteMaterial = new THREE.SpriteMaterial({
        map: texture,
        sizeAttenuation: false,
      });
      this.degreeSprite = new THREE.Sprite(spriteMaterial);
      this.degreeSprite.position.set(0, 0, 0); // Position above bolt
      this.degreeSprite.scale.set(0.15, 0.075, 1);
      
      this.scene.add(this.degreeSprite); // Add degree sprite to scene

      // Logo removed as requested - no longer adding logo overlay
      // this.addLogoOverlay();

      // Add subtle continuous rotation to show 3D nature
      this.addContinuousRotation();

      this.updateRotation();
    },

    addContinuousRotation() {
      // Add a very subtle continuous rotation to show the 3D nature of the bolt
      // This will be a slow rotation around the Y axis
      this.boltAutoRotationSpeed = 0.2; // degrees per second
    },

    updateRotation() {
      if (this.bolt) {
        // In our meter, 0 degrees is at the right (3 o'clock position)
        // For counter-clockwise rotation to match the reversed meter
        let displayRotation = this.rotation;
        let actualRotation;

        if (this.rotationAxis === "y") {
          // For Y-axis, reverse direction for counter-clockwise rotation
          actualRotation = this.rotation - 90;
        } else if (this.rotationAxis === "x") {
          actualRotation = -this.rotation;
        } else {
          actualRotation = -this.rotation;
        }

        const rad = THREE.MathUtils.degToRad(actualRotation);
        if (this.rotationAxis === "x") {
          this.bolt.rotation.x = rad;
        } else if (this.rotationAxis === "y") {
          this.bolt.rotation.y = rad;
        } else {
          this.bolt.rotation.z = rad;
        }

        // Update degree text to show the display rotation value
        this.updateRotationIndicator(displayRotation);

        // Update the path visualization to follow the current rotation
        this.createPathVisualization();
      }
    },

    updateRotationIndicator(displayRotation) {
      // Remove previous indicator if it exists
      if (this.rotationIndicator) {
        this.scene.remove(this.rotationIndicator);
      }

      // Create a new indicator
      const canvas = document.createElement("canvas");
      canvas.width = 128;
      canvas.height = 64;
      const ctx = canvas.getContext("2d");

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw rounded rectangle background
      ctx.fillStyle = "rgba(128, 128, 128, 0.8)";
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 2;
      ctx.beginPath();
      const cornerRadius = 10;
      ctx.roundRect(0, 0, canvas.width, canvas.height, cornerRadius);
      ctx.fill();
      ctx.stroke();

      // Draw text
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 36px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(
        `${Math.round(displayRotation)}°`,
        canvas.width / 2,
        canvas.height / 2
      );

      // Create texture and sprite
      const texture = new THREE.CanvasTexture(canvas);
      const material = new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        depthTest: false,
      });

      // Create sprite
      const sprite = new THREE.Sprite(material);

      // Position outside the meter, pointing to the current rotation value
      // For counter-clockwise rotation with 0 at 3 o'clock, reverse the direction
      const angle = ((-displayRotation - 90) * Math.PI) / 180; // Negative for counter-clockwise, -90 for 3 o'clock start
      const indicatorRadius = 3.4; // Closer to the meter edge (adjusted for smaller meter)
      sprite.position.set(
        Math.cos(angle) * indicatorRadius,
        -1.3, // Match the meter's new position
        Math.sin(angle) * indicatorRadius
      );

      // Scale the sprite
      sprite.scale.set(1.5, 0.7, 1);

      // Store the sprite for later updates
      this.rotationIndicator = sprite;

      // Add to scene
      this.scene.add(this.rotationIndicator);

      // Remove the old degree sprite if it exists
      if (this.degreeSprite) {
        this.scene.remove(this.degreeSprite);
        this.degreeSprite = null;
      }
    },

    createPathVisualization() {
      // Remove existing path visualization first
      if (this.pathLine) {
        this.scene.remove(this.pathLine);
        this.pathLine = null;
      }

      // Create a radial line that will animate from 0° to 42°
      const radius = 3.2; // Same radius as the dial

      // Use the current rotation value for animation purposes
      // For counter-clockwise rotation with 0 at 3 o'clock
      const lineAngle = THREE.MathUtils.degToRad(-this.rotation - 90);

      // Create a new group for the line and sector
      this.pathLine = new THREE.Group();

      // Determine color based on rotation angle
      let color;
      const currentRotation = Math.abs(this.rotation - this.startRotation);
      if (currentRotation < this.thresholds.yellow) {
        color = 0x006600; // Darker Green
      } else if (currentRotation >= this.thresholds.yellow && currentRotation < this.thresholds.red) {
        color = 0x666600; // Darker Yellow
      } else {
        color = 0x600000; // Darker Red
      }

      // Create shaded sector (pie slice) from start rotation to current rotation
      if (Math.abs(this.rotation - this.startRotation) > 0.1) { // Only show sector if there's meaningful rotation
        // DEBUG: Let's start with a simple hardcoded sector to test the concept
        // We want a sector from 12 o'clock (your 0°) to about 1:30 position (your ~45°)
        
        // In Three.js CircleGeometry:
        // 0 = 3 o'clock, π/2 = 6 o'clock, π = 9 o'clock, 3π/2 = 12 o'clock
        
        const startAngle = -Math.PI / 2; // 12 o'clock position (your 0°)
        const sweepAngle = THREE.MathUtils.degToRad(this.rotation - this.startRotation); // Convert your degrees to radians
        
        console.log(`Debug: startRotation=${this.startRotation}, currentRotation=${this.rotation}, sweepAngle=${sweepAngle}`);
        
        // Create the sector geometry
        const sectorGeometry = new THREE.CircleGeometry(
          radius, // radius
          64, // segments for smooth circle
          startAngle * -1, // start at 12 o'clock
          sweepAngle // sweep by the rotation amount
        );
        
        // Create material with transparency and same color as line
        const sectorMaterial = new THREE.MeshBasicMaterial({
          color: color,
          transparent: true,
          opacity: 0.3, // Semi-transparent so you can see the meter behind
          side: THREE.DoubleSide,
          depthWrite: false // Prevent z-fighting issues
        });
        
        // Create the sector mesh
        const sector = new THREE.Mesh(sectorGeometry, sectorMaterial);
        sector.position.y = -1.46; // Position slightly below the meter but above the background
        sector.rotation.x = -Math.PI / 2; // Lay flat on the XY plane
        
        // Add sector to the group first (so it renders behind the line)
        this.pathLine.add(sector);
      }

      // Add a wider line for better visibility using a plane geometry
      const thickLineGeometry = new THREE.BufferGeometry();

      // Create a plane geometry that looks like a thick line
      const width = 0.18; // Increased thickness for better visibility (was 0.09)
      const halfWidth = width / 2;

      // Calculate perpendicular vector for width
      const perpX = -Math.sin(lineAngle);
      const perpZ = Math.cos(lineAngle);

      // Create vertices for a quad
      const vertices = new Float32Array([
        // First vertex (center + perpendicular offset)
        halfWidth * perpX,
        0,
        halfWidth * perpZ,

        // Second vertex (end point + perpendicular offset)
        Math.cos(lineAngle) * radius + halfWidth * perpX,
        0,
        Math.sin(lineAngle) * radius + halfWidth * perpZ,

        // Third vertex (end point - perpendicular offset)
        Math.cos(lineAngle) * radius - halfWidth * perpX,
        0,
        Math.sin(lineAngle) * radius - halfWidth * perpZ,

        // Fourth vertex (center - perpendicular offset)
        -halfWidth * perpX,
        0,
        -halfWidth * perpZ,
      ]);

      // Create face indices
      const indices = [0, 1, 2, 0, 2, 3];

      // Set geometry attributes
      thickLineGeometry.setAttribute(
        "position",
        new THREE.BufferAttribute(vertices, 2.5)
      );
      thickLineGeometry.setIndex(indices);

      // Create material with dynamic color based on rotation (same color as sector)
      const thickLineMaterial = new THREE.MeshBasicMaterial({
        color: color,
        side: THREE.DoubleSide,
      });

      // Create mesh
      const thickLine = new THREE.Mesh(thickLineGeometry, thickLineMaterial);
      thickLine.position.y = -1.44; // Position above the meter to be visible (above the sector)

      // Add line to the group (after sector so it renders on top)
      this.pathLine.add(thickLine);

      // Add to scene
      this.scene.add(this.pathLine);
    },

    animate() {
      this.animationId = requestAnimationFrame(this.animate);

      if (this.isAnimating) {
        const duration = 1.5; // Animation duration in seconds (1-2 seconds as requested)
        const elapsedTime = this.clock.getElapsedTime();
        const progress = Math.min(elapsedTime / duration, 1);

        // Ease in-out function
        const easeProgress =
          progress < 0.5
            ? 2 * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 2) / 2;

        // Calculate and update rotation
        const newRotation =
          this.startRotation +
          (this.endRotation - this.startRotation) * easeProgress;

        if (Math.abs(newRotation - this.rotation) > 0.01) {
          this.$emit("update:rotation", newRotation);

          // Update the path visualization to show the sweeping line during animation
          this.createPathVisualization();
        }

        // Check if animation is complete
        if (progress >= 1) {
          this.isAnimating = false;
          this.$emit("update:is-animating", false);
          this.$emit("animation-complete");

          // Ensure the line is at exactly 42° at the end of animation
          this.$emit("update:rotation", this.endRotation);
          this.createPathVisualization();
        }
      } else if (this.autoRotate && this.bolt) {
        const delta = this.clock.getDelta();
        const rotateAmount = delta * this.rotationSpeed;
        const newRotation = (this.rotation - rotateAmount * 30) % 360; // Negative for counter-clockwise
        this.$emit("update:rotation", newRotation);

        // Update path visualization if auto-rotating
        this.createPathVisualization();
      }

      // Add subtle continuous rotation to the bolt assembly to show 3D nature
      if (this.bolt && this.boltAutoRotationSpeed > 0) {
        const delta = this.clock.getDelta();
        const rotationIncrement = this.boltAutoRotationSpeed * delta * (Math.PI / 180); // Convert to radians
        
        // Only rotate the bolt assembly around Y-axis for subtle 3D effect
        this.bolt.rotation.y += rotationIncrement;
      }

      // Update degree numbers to maintain their orientation
      if (this.degreesGroup) {
        // Get the current Y rotation of the gridHelper
        const meterRotation = this.gridHelper ? this.gridHelper.rotation.z : 0;

        // Update each sprite's rotation to counter the meter rotation
        this.degreesGroup.children.forEach((sprite) => {
          // Counter-rotate each sprite to maintain upright orientation
          sprite.material.rotation = -meterRotation;
        });
      }

      this.controls.update();
      this.renderer.render(this.scene, this.camera);
    },

    startBoltAnimation() {
      this.clock.start();
    },

    updateRotationAxis() {
      // Only reset rotations if the axis has changed
      if (this.rotationAxis !== this.previousRotationAxis) {
        // Reset rotations when changing axis
        this.bolt.rotation.set(0, 0, 0);
        this.$emit("update:rotation", this.startRotation);
        this.previousRotationAxis = this.rotationAxis;
      }
    },

    onWindowResize() {
      if (this.camera && this.renderer && this.$refs.threeContainer) {
        this.camera.aspect =
          this.$refs.threeContainer.clientWidth /
          this.$refs.threeContainer.clientHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(
          this.$refs.threeContainer.clientWidth,
          this.$refs.threeContainer.clientHeight
        );
      }
    },

    replayAnimation() {
      // Reset clock and start animation
      this.clock = new THREE.Clock();
      // Set rotation back to start
      this.$emit("update:rotation", this.startRotation);
      // Trigger animation - notify parent component
      this.$emit("animation-complete", false); // First clear the complete status
      this.$emit("update:is-animating", true); // Tell parent to set isAnimating=true (for v-model)
      // Also set local isAnimating to true for direct usage
      this.isAnimating = true;
      this.startBoltAnimation();
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.initThree();
      // Start animation automatically after a short delay to ensure scene is ready
      setTimeout(() => {
        // Force reset rotation to start position
        this.$emit("update:rotation", this.startRotation);
        // Force local isAnimating state to true regardless of prop
        this.isAnimating = true;
        // Notify parent about animation starting
        this.$emit("update:is-animating", true);
        // Start animation with fresh clock
        this.clock = new THREE.Clock();
        this.startBoltAnimation();
      }, 500);
    });
  },
  beforeDestroy() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }

    window.removeEventListener("resize", this.onWindowResize);

    // Dispose of resources
    if (this.renderer) {
      this.renderer.dispose();
    }

    // Remove canvas from DOM
    if (this.$refs.threeContainer && this.renderer) {
      this.$refs.threeContainer.removeChild(this.renderer.domElement);
    }
  },
};
</script>

<style scoped>
.three-container {
  width: 100%;
  height: 100%;
}

.scene-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 8px;
}

.info-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none; /* Allow clicks to pass through to the 3D scene */
  z-index: 1;
}

.sensor-name {
  color: #fff;
  font-size: 24px;
  font-weight: regular;
  text-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
  top: 15px;
  text-align: center;
  z-index: 5;
}

.scene-replay-button {
  position: absolute;
  bottom: 20px;
  right: 5%;
  transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.9);
  color: white;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
  z-index: 2;
  pointer-events: auto;
  transition: all 0.3s ease;
  box-shadow: 0 3px 5px rgba(0, 0, 0, 0.3);
}

.scene-replay-button:hover {
  background-color: #000;
  transform: translateX(-50%) scale(1.1);
}

.left-cards {
  position: absolute;
  left: 20px;
  top: 12px;
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.info-card {
  color: #fff;
  background-color: rgba(128, 128, 128, 0.8);
  padding: 10px 15px;
  border-radius: 8px;
  text-align: left;
  width: 250px;
}

.card-title {
  font-weight: bold;
  font-size: 14px;
  margin-bottom: 8px;
  color: rgba(255, 255, 255, 0.9);
  text-transform: uppercase;
  letter-spacing: 1px;
}

.metric {
  margin-bottom: 6px;
}

.metric .label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 2px;
}

.metric .value {
  font-size: 16px;
  font-weight: 500;
}

.metric.status .value.warning {
  font-size: 18px;
  font-weight: 600;
}

.metric.status .value.secure {
  font-size: 18px;
  font-weight: 600;
}

.metric.status .value.critical {
  font-size: 18px;
  font-weight: 600;
}

.metric.risk .value {
  font-size: 12px;
  color: #ff4444;
  margin-top: 4px;
}

.right-cards {
  position: absolute;
  right: 20px;
  top: 12px;
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.inspection-info {
  position: relative;
  display: flex;
  flex-direction: column;
  width: auto;
}

.inspection-cards {
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 250px;
}

.last-inspection,
.next-inspection {
  color: #fff;
  background-color: rgba(128, 128, 128, 0.8);
  padding: 10px 15px;
  border-radius: 8px;
  text-align: right;
  width: 100%;
}

.last-inspection .label,
.next-inspection .label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 4px;
}

.last-inspection .value,
.next-inspection .value {
  font-size: 18px;
  font-weight: 500;
}

.full-scene-container {
  position: relative;
  width: 100%;
  height: 800px;
  box-shadow: 0 20px 27px 0 rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.metric.status .value {
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 600;
}

.info-card.torque-rotation:hover {
  background-color: rgba(0, 0, 0, 0.6);
  transition: background-color 0.3s ease;
}

.details-hint {
  font-size: 12px;
  opacity: 0.7;
  font-style: italic;
}

.info-card.torque-rotation:hover .details-hint {
  opacity: 1;
}

.status-details-btn {
  cursor: pointer;
  pointer-events: auto;
}

.status-details-btn:hover {
  background-color: rgba(0, 0, 0, 0.8);
  transform: scale(1.05);
}
</style>
